import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Handles the DPDP children's-data consent gate before uploading.
export function MediaUploader({ label, kind = "gallery", multiple = false, value = [], onChange }) {
  const inputRef = useRef();
  const consentRef = useRef();
  const [pending, setPending] = useState(null); // File awaiting minor decision
  const [containsMinors, setContainsMinors] = useState(false);
  const [consentFile, setConsentFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPending(file);
    setContainsMinors(false);
    e.target.value = "";
  };

  const doUpload = async ({ hasConsent = false, autoBlur = false } = {}) => {
    if (!pending) return;
    setUploading(true);
    try {
      if (hasConsent && consentFile) {
        const cfd = new FormData();
        cfd.append("file", consentFile);
        cfd.append("kind", "consent");
        await api.post("/ngo/media", cfd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const fd = new FormData();
      fd.append("file", pending);
      fd.append("kind", kind);
      fd.append("contains_minors", containsMinors);
      fd.append("has_consent", hasConsent);
      fd.append("auto_blur", autoBlur);
      const { data } = await api.post("/ngo/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(multiple ? [...value, data.url] : data.url);
      toast.success(data.blurred ? "Uploaded with faces blurred" : hasConsent ? "Uploaded with consent record on file" : "Uploaded");
      setPending(null); setContainsMinors(false); setConsentFile(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const remove = (url) => onChange(multiple ? value.filter((u) => u !== url) : "");
  const items = multiple ? value : value ? [value] : [];

  return (
    <div data-testid={`media-uploader-${kind}`}>
      {label && <label className="text-sm mb-1.5 block font-medium">{label}</label>}
      <div className="flex flex-wrap gap-3">
        {items.map((url) => (
          <div key={url} className="relative w-24 h-24 rounded-lg overflow-hidden border border-stone-200 group">
            <img src={url} alt="upload" className="w-full h-full object-cover" />
            <button type="button" onClick={() => remove(url)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5" data-testid="media-remove"><X size={13} /></button>
          </div>
        ))}
        <button type="button" onClick={pick} className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center gap-1 text-stone-500 hover:border-terracotta hover:text-terracotta transition-colors" data-testid="media-pick-btn">
          <Upload size={18} /><span className="text-xs">Upload</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="media-file-input" />

      {pending && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">{pending.name}</p>
          <label className="flex items-center gap-2 text-sm text-stone-700 mt-2 cursor-pointer">
            <input type="checkbox" checked={containsMinors} onChange={(e) => setContainsMinors(e.target.checked)} data-testid="media-minors-check" />
            This image shows identifiable children (minors)
          </label>
          {containsMinors ? (
            <div className="mt-3 space-y-2">
              <div className="flex items-start gap-2 text-xs text-amber-800"><ShieldAlert size={14} className="shrink-0 mt-0.5" /> DPDP Rule 10: a minor's photo needs verifiable guardian consent. Choose one:</div>
              <div className="flex flex-wrap gap-2">
                <input ref={consentRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setConsentFile(e.target.files?.[0] || null)} data-testid="consent-file-input" />
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => consentRef.current?.click()} data-testid="attach-consent-btn">
                  {consentFile ? `✓ ${consentFile.name.slice(0, 18)}` : "Attach guardian-consent document"}
                </Button>
                <Button size="sm" disabled={uploading || !consentFile} onClick={() => doUpload({ hasConsent: true })} className="bg-terracotta hover:bg-terracotta-hover" data-testid="media-consent-btn">
                  {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null} Upload photo (keep faces)
                </Button>
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => doUpload({ autoBlur: true })} data-testid="media-blur-btn">
                  Auto-blur faces instead
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 mt-3">
              <Button size="sm" disabled={uploading} onClick={() => doUpload()} className="bg-terracotta hover:bg-terracotta-hover" data-testid="media-upload-confirm">
                {uploading ? <Loader2 size={14} className="mr-1 animate-spin" /> : null} Upload
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPending(null)} data-testid="media-cancel">Cancel</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MediaUploader;
